import { useQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef, useContext } from 'react';
import ReactDOM from 'react-dom';
import { AppContext } from 'shared/AppContext';
import {
  ModalBackdrop,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  SearchBox,
  SearchIcon,
  SearchInput,
  DiscountList,
  DiscountItem,
  DiscountCode,
  DiscountDetails,
  ModalFooter,
  Button,
  LoadingContainer,
  EmptyState,
} from './StyledComponents';

const GET_PROMOS_BY_ACCOUNT = gql`
  query GetPromosByAccount($input: GetPromosByAccountInput!) {
    getPromosByAccount(input: $input) {
      name
      expiration
      ticket_discount
    }
  }
`;

const DiscountSelectionModal = ({
  isOpen,
  onClose,
  onSelect,
  selectedDiscountCode,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const modalRef = useRef(null);
  const { state } = useContext(AppContext);

  // Get account ID from context
  const accountId = state?.account?.id || state?.accountId;

  // Query for discount codes
  const { data, loading, error } = useQuery(GET_PROMOS_BY_ACCOUNT, {
    variables: {
      input: {
        account_id: accountId,
      },
    },
    skip: !isOpen || !accountId,
    fetchPolicy: 'network-only',
  });

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Set initial selected discount
  useEffect(() => {
    if (selectedDiscountCode && data?.getPromosByAccount) {
      const discount = data.getPromosByAccount.find(
        (p) => p.name === selectedDiscountCode
      );
      if (discount) {
        setSelectedDiscount(discount);
      }
    }
  }, [selectedDiscountCode, data]);

  if (!isOpen) return null;

  // Filter discounts by search term
  const filterDiscounts = (discounts) => {
    if (!discounts) return [];

    return discounts.filter((discount) => {
      if (!searchTerm) return true;
      const name = discount.name?.toLowerCase() || '';
      const search = searchTerm.toLowerCase();
      return name.includes(search);
    });
  };

  const handleDiscountSelect = (discount) => {
    setSelectedDiscount(discount);
  };

  const handleConfirm = () => {
    if (selectedDiscount) {
      onSelect(selectedDiscount);
      onClose();
    }
  };

  const formatExpiration = (expiration) => {
    if (!expiration) return 'No expiration';
    const date = new Date(expiration);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDiscount = (ticketDiscount) => {
    if (!ticketDiscount) return '';
    // Assuming ticket_discount is a number representing dollars
    return `$${ticketDiscount} off`;
  };

  const discounts = data?.getPromosByAccount || [];
  const filteredDiscounts = filterDiscounts(discounts);

  return ReactDOM.createPortal(
    <ModalBackdrop>
      <ModalContainer ref={modalRef}>
        <ModalHeader>
          <ModalTitle>Select Discount Code</ModalTitle>
          <SearchBox>
            <SearchIcon>
              <svg
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search discount codes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBox>
        </ModalHeader>

        <DiscountList>
          {loading && (
            <LoadingContainer>
              <div>Loading discount codes...</div>
            </LoadingContainer>
          )}

          {error && (
            <EmptyState>
              <div>Error loading discount codes. Please try again.</div>
            </EmptyState>
          )}

          {!loading && !error && filteredDiscounts.length === 0 && (
            <EmptyState>
              <div>No discount codes found</div>
              {searchTerm && <div>Try adjusting your search</div>}
              {!searchTerm && (
                <div>Create discount codes in your event settings</div>
              )}
            </EmptyState>
          )}

          {!loading &&
            !error &&
            filteredDiscounts.map((discount, index) => (
              <DiscountItem
                key={index}
                selected={selectedDiscount?.name === discount.name}
                onClick={() => handleDiscountSelect(discount)}
              >
                <DiscountCode>{discount.name}</DiscountCode>
                <DiscountDetails>
                  {formatDiscount(discount.ticket_discount)}
                  {discount.expiration && (
                    <>
                      {discount.ticket_discount && ' • '}
                      Expires: {formatExpiration(discount.expiration)}
                    </>
                  )}
                </DiscountDetails>
              </DiscountItem>
            ))}
        </DiscountList>

        <ModalFooter>
          <Button onClick={onClose}>Cancel</Button>
          <Button primary onClick={handleConfirm} disabled={!selectedDiscount}>
            Select Code
          </Button>
        </ModalFooter>
      </ModalContainer>
    </ModalBackdrop>,
    document.body
  );
};

DiscountSelectionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  selectedDiscountCode: PropTypes.string,
};

export default DiscountSelectionModal;
